/**
 * Wear-With-AI navigation intent — the listing + outfit image the
 * user clicked "Wear with AI" on, persisted across the studio's
 * onboarding chain so the user-task survives a setup detour.
 *
 * Why sessionStorage instead of module memory:
 *   `useNavigateToWearWithAi` uses `window.location.href` (a hard
 *   navigation) to escape the @modal interceptor. That tears down
 *   the JS module along with the rest of the app shell, so a
 *   module-level `let _intent` (like legacy's `navigationIntent.ts`)
 *   would not survive. `sessionStorage` is the right scope: same
 *   tab, cleared when the tab closes, persists across hard navs
 *   AND across the studio's multi-page setup chain (intro →
 *   setup-outfit → select-style → ...).
 *
 * Why not query params:
 *   The query params on `/ai-creator-studio/look` get DROPPED when
 *   the studio redirects through the persona-setup chain. Each
 *   intermediate page is its own route push and the `?wearWithAi…`
 *   suffix doesn't carry through. sessionStorage survives the
 *   chain; the final consumer (the outfit step) reads it then.
 */

const STORAGE_KEY = 'wearWithAi.intent';

export interface WearWithAiIntent {
  listingId: string;
  outfitImageUrl?: string;
}

export function setWearWithAiIntent(intent: WearWithAiIntent): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // Storage quota / disabled / privacy mode — silently ignored.
    // The studio still works, the user just doesn't get the
    // pre-selected outfit.
  }
}

/**
 * Read + clear the pending intent. Matches the legacy
 * `consumeCreatorStudioProductIntent` semantics: each consumption
 * empties the slot so a second visit to the studio doesn't
 * re-trigger an old product flow.
 */
export function consumeWearWithAiIntent(): WearWithAiIntent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as WearWithAiIntent;
    if (!parsed || typeof parsed.listingId !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Peek at the pending intent without clearing it. Useful for the
 * onboarding chain's intermediate pages (intro, select-style)
 * that want to know "we're in the middle of a wear-with-ai task"
 * without burning the intent — the outfit step is the only
 * consumer that actually clears it.
 */
export function peekWearWithAiIntent(): WearWithAiIntent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WearWithAiIntent;
    if (!parsed || typeof parsed.listingId !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}
